import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { admin_base_url } from "../Global";
import { ROLE_PERMISSIONS } from "../adminConfig";

const AuthContext = createContext(null);

const readStorage = () => {
  try {
    return {
      token: localStorage.getItem("accessToken"),
      user: JSON.parse(localStorage.getItem("user") || "null"),
    };
  } catch {
    return { token: null, user: null };
  }
};

const resolvePermissions = (user) => {
  if (!user) return [];
  if (user.role === "SUPER_ADMIN") return Object.values(ROLE_PERMISSIONS.SUPER_ADMIN || {});
  if (Array.isArray(user.permissions)) {
    return user.permissions;
  }
  return ROLE_PERMISSIONS[user.role] || [];
};

const fetchFullProfile = async (token, jwtPayload, loginResponseData = null) => {
  if (loginResponseData) {
    const candidate =
      loginResponseData.admin ||
      loginResponseData.user ||
      loginResponseData.data ||
      loginResponseData.result ||
      loginResponseData.profile;
    if (candidate && typeof candidate === "object") return candidate;
  }

  let fetchEmail = jwtPayload?.email;
  try {
    const cachedUser = JSON.parse(localStorage.getItem("user"));
    if (cachedUser && cachedUser.email) {
      fetchEmail = cachedUser.email;
    }
  } catch (e) { }

  if (fetchEmail) {
    try {
      const res = await fetch(`${admin_base_url}/get-by-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: fetchEmail }),
      });
      if (res.ok) {
        const data = await res.json();
        const profile = data.data || data.user || data;
        if (profile?.role) return profile;
      }
    } catch { }
  }

  return null;
};

export const AuthProvider = ({ children }) => {
  const { token: storedToken, user: storedUser } = readStorage();

  const [state, setState] = useState({
    token: storedToken,
    user: storedUser,
    permissions: resolvePermissions(storedUser),
    isRefreshing: !!storedToken,
  });

  const hasMounted = useRef(false);

  const logout = useCallback(() => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("user");
    setState({ token: null, user: null, permissions: [], isRefreshing: false });
  }, []);

  const login = useCallback(
    async (token, jwtPayload, loginResponseData = null) => {
      const fullProfile = await fetchFullProfile(token, jwtPayload, loginResponseData);

      if (fullProfile && (fullProfile.isBlockedBySuperAdmin || fullProfile.status === "blocked")) {
        logout();
        throw new Error("This account has been blocked by the system administrator.");
      }

      localStorage.setItem("accessToken", token);

      const user = fullProfile ? { ...jwtPayload, ...fullProfile } : jwtPayload;
      const permissions = resolvePermissions(user);
      localStorage.setItem("user", JSON.stringify({ ...user, permissions }));
      setState({ token, user, permissions, isRefreshing: false });
    },
    [logout],
  );

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    let jwtPayload = {};
    try {
      jwtPayload = JSON.parse(atob(token.split(".")[1]));
    } catch {
      logout();
      return;
    }

    const fullProfile = await fetchFullProfile(token, jwtPayload, null);

    if (!fullProfile || fullProfile.isBlockedBySuperAdmin || fullProfile.status === "blocked") {
      logout();
      return;
    }

    const user = { ...jwtPayload, ...fullProfile };
    const permissions = resolvePermissions(user);
    const merged = { ...user, permissions };

    localStorage.setItem("user", JSON.stringify(merged));
    setState((prev) => ({ ...prev, user: merged, permissions, isRefreshing: false }));
  }, [logout]);

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;

    if (storedToken) {
      refreshUser().catch(() => {
        setState((prev) => ({ ...prev, isRefreshing: false }));
      });
    }
  }, [storedToken, refreshUser]);

  useEffect(() => {
    const onFocus = () => {
      if (localStorage.getItem("accessToken")) refreshUser();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshUser]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
